import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { useEmbedded } from '@/shared/embedded';

import { placeBubble } from './infoTipPlacement';
import type { Placement } from './infoTipPlacement';

/* A short explanation of a term, behind a small "i" beside it.
 *
 * **What this is for, and what it is deliberately not for.** It explains a *word* — what
 * "Reconciliation" or "Completeness" means — for a reader who has not met it. It never carries
 * state, a reason something failed, or anything a reader must see to act correctly. This codebase
 * records that rule in at least eight places ("acknowledged is not resolved, and that does not
 * belong in a tooltip"), because hover-only text is unreachable by touch and invisible to anybody
 * who does not think to look. If the sentence matters to the decision on the screen, it belongs on
 * the screen.
 *
 * **One glyph for the whole product.** The trigger reuses `.lq-hintbtn`, the "i" the access-policy
 * form already shows beside its bundle name, so a reader meets one icon meaning "there is an
 * explanation here" rather than two. That form's `AuthzNameInfo` stays a click-to-open disclosure:
 * it holds two paragraphs mid-form, and a reader who tabs into it to read must not have it vanish
 * on blur — which is the opposite of what a tooltip does.
 *
 * Four ways in, one way the pointer and keyboard agree:
 *
 * * **Hover** (mouse only) opens after a short delay, so sweeping the pointer across a table does
 *   not flash a tip on every header it crosses. Moving onto the bubble keeps it open (WCAG 1.4.13,
 *   "hoverable").
 * * **Keyboard focus** opens it at once. Focus that follows a pointer press does not, which is what
 *   stops a tap from opening on focus and then closing on the click that follows.
 * * **Tap** toggles it, because a touch screen has no hover and a tip that only hover could open
 *   would not exist there. A tap anywhere else closes it.
 * * **Escape** closes it from anywhere without moving focus (1.4.13, "dismissible"), and stops
 *   there rather than also closing a dialog the tip happens to sit in.
 *
 * **The text is the button's accessible description, held in a `hidden` span that is always in the
 * DOM.** A description that existed only while the bubble was open would race the focus event that
 * opens it, and a screen reader could announce the button before the text arrived. `hidden`
 * elements remain valid `aria-describedby` targets, so the description is there from the first
 * render; the visible bubble is the sighted reader's copy of the same words.
 *
 * **One tip open at a time, across the page.** Opening a tip closes any other, whichever way
 * each was opened — a keyboard-focused tip and a hover-opened one would otherwise stack two
 * bubbles over a table header row.
 *
 * **No `aria-expanded`.** The bubble is not a region a reader moves into, so announcing the button
 * as "collapsed" would promise a disclosure that is not there. The description is what a screen
 * reader needs, and it is present whether or not the bubble is.
 *
 * **Portaled, and to the nearest open `<dialog>` when there is one.** Tables here scroll inside
 * their own box (`.lq-table-scroll`), so an absolutely positioned bubble would be clipped at the
 * edge of the very headers that most need it. A portal escapes that — but `Modal` is a
 * `showModal()` dialog in the browser's top layer, and a bubble portaled to `<body>` would render
 * *behind* it. So the container is the trigger's own dialog if it has one.
 */

export interface InfoTipProps {
  /** What is being explained — the visible label beside the icon, e.g. "Completeness". Used for
   * the button's accessible name ("About Completeness"), so it must be the term, not a sentence. */
  readonly subject: string;
  /** The explanation, already translated. One or two short sentences: purpose, meaning or effect.
   * Not the label restated. */
  readonly text: string;
  /** Preferred side. Flipped automatically when there is no room, so this is a preference. */
  readonly side?: 'top' | 'bottom';
}

/* Room for a "Learn more" link, deliberately not built yet. The guide (`features/guide`) is the
 * only documentation in the product and has no way to open at a topic, so there is nothing to link
 * to. When there is, the shape is a `learnMore` prop rendered after `.lq-infotip__text` — and the
 * tip must then stop closing on blur, because a link inside a bubble that vanishes when focus leaves
 * the button is a link no keyboard user can reach. That is a toggletip, not a tooltip, and it is a
 * change to this component's behaviour rather than a slot to fill. */

/* The one tip that is open, so opening another can close it. Module scope rather than context: the
 * coordination is page-wide and has no state worth rendering. */
let closeOpenTip: (() => void) | null = null;

/* Long enough that a pointer passing over a row of headers on its way somewhere else opens
 * nothing; short enough that one deliberately resting on the icon does not feel ignored. */
const OPEN_DELAY_MS = 150;
/* The grace period for the pointer to cross the gap between icon and bubble. */
const CLOSE_DELAY_MS = 120;
export function InfoTip({ subject, text, side = 'top' }: InfoTipProps): ReactElement {
  const { t } = useTranslation();
  const id = useId();
  const descriptionId = `${id}-desc`;
  const bubbleId = `${id}-tip`;

  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const embedded = useEmbedded();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const timer = useRef<number | undefined>(undefined);
  /* The pointer type of a press that is about to focus the button, or '' when focus arrives some
   * other way. Cleared on blur, so every focus is judged afresh. */
  const pressedWith = useRef('');

  const clearTimer = useCallback(() => {
    if (timer.current !== undefined) window.clearTimeout(timer.current);
    timer.current = undefined;
  }, []);

  const openAfter = useCallback(
    (delay: number) => {
      clearTimer();
      if (delay === 0) setOpen(true);
      else
        timer.current = window.setTimeout(() => {
          setOpen(true);
        }, delay);
    },
    [clearTimer],
  );

  const closeAfter = useCallback(
    (delay: number) => {
      clearTimer();
      if (delay === 0) setOpen(false);
      else
        timer.current = window.setTimeout(() => {
          setOpen(false);
        }, delay);
    },
    [clearTimer],
  );

  useEffect(() => clearTimer, [clearTimer]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (): void => {
      clearTimer();
      setOpen(false);
    };
    if (closeOpenTip !== null) closeOpenTip();
    closeOpenTip = close;
    return () => {
      if (closeOpenTip === close) closeOpenTip = null;
    };
  }, [open, clearTimer]);

  /* Measure after the bubble is in the DOM and before the browser paints it, so it never flashes
   * at the origin. Re-measured on scroll and resize while open, because a fixed-position bubble
   * does not travel with a scrolled table. */
  useLayoutEffect(() => {
    if (!open) {
      setPlacement(null);
      return undefined;
    }
    const measure = (): void => {
      const button = triggerRef.current;
      const bubble = bubbleRef.current;
      if (button === null || bubble === null) return;
      const anchor = button.getBoundingClientRect();
      /* An icon scrolled out of view takes its bubble with it, rather than leaving the bubble pinned
       * to the viewport edge explaining something the reader can no longer see. */
      if (anchor.bottom < 0 || anchor.top > window.innerHeight) {
        setOpen(false);
        return;
      }
      setPlacement(
        placeBubble(
          anchor,
          bubble.getBoundingClientRect(),
          { width: window.innerWidth, height: window.innerHeight },
          side,
        ),
      );
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, side]);

  /* Escape from anywhere, and a press outside, while open. Listening on the document rather than
   * the button is what makes a hover-opened tip dismissible without first moving focus to it. */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      /* Stopped so a tip inside a `Modal` closes alone: the dialog's own Escape handling would
       * otherwise take the whole dialog away to dismiss one sentence. */
      event.stopPropagation();
      event.preventDefault();
      closeAfter(0);
    };
    const onPress = (event: PointerEvent): void => {
      const target = event.target as Node | null;
      if (target === null) return;
      if (triggerRef.current?.contains(target) === true) return;
      if (bubbleRef.current?.contains(target) === true) return;
      closeAfter(0);
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPress, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPress, true);
    };
  }, [open, closeAfter]);

  /* A tip inside an open `<dialog>` must render in it, or the modal's top layer hides it. Otherwise
     the page: `document.body` standalone, and inside the Suite shell the portal container the shell
     provides within Lumiq's root, where Lumiq's scoped styles reach it. */
  const container =
    typeof document === 'undefined'
      ? null
      : (triggerRef.current?.closest('dialog') ??
        embedded?.portalContainer ??
        document.body);

  const bubbleStyle: CSSProperties =
    placement === null
      ? { top: 0, left: 0, visibility: 'hidden' }
      : { top: placement.top, left: placement.left };

  return (
    <span className="lq-infotip">
      <button
        ref={triggerRef}
        type="button"
        className="lq-hintbtn lq-infotip__trigger"
        aria-label={t('help.about', { subject })}
        aria-describedby={descriptionId}
        /* State for the stylesheet only — see the note on `aria-expanded` above. */
        data-open={open ? '' : undefined}
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse') openAfter(OPEN_DELAY_MS);
        }}
        onPointerLeave={(event) => {
          if (event.pointerType === 'mouse') closeAfter(CLOSE_DELAY_MS);
        }}
        onPointerDown={(event) => {
          pressedWith.current = event.pointerType;
        }}
        onFocus={() => {
          if (pressedWith.current === '') openAfter(0);
        }}
        onBlur={() => {
          pressedWith.current = '';
          closeAfter(0);
        }}
        onClick={(event) => {
          /* Never the parent's click as well: the icon sits beside sort buttons, inside clickable
           * rows and cards, and explaining a column must not also re-sort it. */
          event.stopPropagation();
          const how = pressedWith.current;
          /* A mouse click on an icon the pointer is already resting on keeps the tip open — a
           * toggle would close it at the moment somebody clicked to read it. Touch, pen and the
           * keyboard (Enter/Space, which focus already opened) toggle. */
          if (how === 'mouse') openAfter(0);
          else setOpen((shown) => !shown);
        }}
      >
        i
      </button>
      <span id={descriptionId} hidden>
        {text}
      </span>
      {open &&
        container !== null &&
        createPortal(
          <span
            ref={bubbleRef}
            id={bubbleId}
            role="tooltip"
            className={`lq-infotip__bubble lq-infotip__bubble--${placement?.side ?? side}`}
            style={bubbleStyle}
            onPointerEnter={(event) => {
              if (event.pointerType === 'mouse') clearTimer();
            }}
            onPointerLeave={(event) => {
              if (event.pointerType === 'mouse') closeAfter(CLOSE_DELAY_MS);
            }}
          >
            <span className="lq-infotip__text">{text}</span>
          </span>,
          container,
        )}
    </span>
  );
}

/** A label with its explanation beside it — the shape every kit component uses for its `help`
 * prop, so the icon sits the same distance from a page title, a section heading, a metric label
 * and a column header.
 *
 * **The icon is a sibling of the label element, never inside it.** A button inside a heading joins
 * the heading's accessible name ("Lineage About Lineage"), and one inside a `<label>` both joins the
 * input's name and activates the input when pressed. Callers wrap; they do not nest.
 *
 * With no `help` it renders its child alone, so a screen that does not opt in gets the exact DOM it
 * had before — no wrapper, no layout change. */
export function HelpLabel({
  subject,
  help,
  className = 'lq-infotip-row',
  inline = false,
  children,
}: {
  readonly subject: string;
  readonly help: string | undefined;
  readonly className?: string;
  /** A `span` rather than a `div`, for inline content such as a badge inside a sentence. */
  readonly inline?: boolean;
  readonly children: ReactElement;
}): ReactElement {
  if (help === undefined) return children;
  /* A `div` by default, because the label it wraps may be a heading and a heading cannot sit in a
     `span`. Inline-flex either way, so it still follows a right-aligned header cell's `text-align`. */
  const Wrapper = inline ? 'span' : 'div';
  return (
    <Wrapper className={className}>
      {children}
      <InfoTip subject={subject} text={help} />
    </Wrapper>
  );
}
